// In-place ICE restart for a live media call, so a network blip (Wi-Fi <-> LTE
// handoff, NAT rebind) recovers in about a second instead of a full re-dial.
//
// PeerJS has no renegotiation path and, worse, tears the peer connection down
// the instant ICE fails (its oniceconnectionstatechange closes the connection).
// We work around both without forking it:
//   - Override pc.oniceconnectionstatechange so a failure drives a restart
//     instead of a teardown. PeerJS assigns it as a property, so our assignment
//     wins; we keep a reference and forward the harmless states.
//   - Carry the renegotiation SDP over the signaling broker as custom message
//     types. The broker relays any type verbatim and PeerJS surfaces every
//     inbound frame on peer.socket 'message', so this needs no PeerJS changes.
//
// The parent is always the offerer (peer.call) and the monitor always answers,
// so there is no glare: the parent owns the restart, the monitor responds. Both
// sides attach a controller — the monitor's is passive (it waits for the offer)
// but still needed to stop PeerJS closing the monitor's side on ICE failure.
//
// If anything goes wrong — an old peer that ignores the custom messages, a
// restart that doesn't reach `connected` in time, or repeated failures — we call
// onGiveUp(), which is the caller's existing full re-dial / drop path. Worst case
// is today's behaviour plus a few seconds, only on connections already failing.

import { track, EVENTS } from './analytics.js';

const MSG_OFFER = 'PB-ICE-RESTART-OFFER';
const MSG_ANSWER = 'PB-ICE-RESTART-ANSWER';

const DISCONNECT_GRACE_MS = 1500; // let a brief 'disconnected' self-heal first
const RESTART_TIMEOUT_MS = 6000;  // offerer: give up on a restart that stalls
const ANSWERER_WAIT_MS = 10000;   // answerer: give up if no offer ever arrives
const MAX_ATTEMPTS = 2;           // offerer restart attempts before onGiveUp

// connectionId -> controller, so a single per-peer socket listener can route an
// inbound offer/answer to the right call among many (the monitor fans out).
const controllers = new Map();
const boundPeers = new WeakSet();

function bindSocket(peer) {
  if (!peer || !peer.socket || boundPeers.has(peer)) return;
  boundPeers.add(peer);
  peer.socket.on('message', (msg) => {
    if (!msg || (msg.type !== MSG_OFFER && msg.type !== MSG_ANSWER)) return;
    const payload = msg.payload || {};
    const ctrl = payload.connectionId && controllers.get(payload.connectionId);
    if (!ctrl) return;
    if (msg.type === MSG_OFFER) ctrl._onRemoteOffer(msg.src, payload);
    else ctrl._onRemoteAnswer(msg.src, payload);
  });
}

// role: 'offerer' (parent) | 'answerer' (monitor)
export function attachIceRecovery(pc, { peer, remoteId, connectionId, role, onGiveUp, onRecovered }) {
  if (!pc || !peer || !connectionId) return null;
  bindSocket(peer);

  const savedIceState = pc.oniceconnectionstatechange;
  const savedIceCandidate = pc.onicecandidate;

  let destroyed = false;
  let recovering = false;
  let attempts = 0;
  let graceTimer = null;
  let watchdog = null;

  const clearTimers = () => {
    clearTimeout(graceTimer); graceTimer = null;
    clearTimeout(watchdog); watchdog = null;
  };

  const giveUp = (reason) => {
    if (destroyed) return;
    clearTimers();
    recovering = false;
    track(EVENTS.ICE_RESTART, { role, outcome: 'give_up', reason });
    if (typeof onGiveUp === 'function') onGiveUp(reason);
  };

  const succeed = () => {
    clearTimers();
    const wasRecovering = recovering;
    recovering = false;
    attempts = 0;
    if (wasRecovering) {
      track(EVENTS.ICE_RESTART, { role, outcome: 'recovered' });
      if (typeof onRecovered === 'function') onRecovered();
    }
  };

  // Re-arm PeerJS's own candidate sender (it clears it on ICE 'completed') so the
  // restart's fresh candidates trickle out over the normal CANDIDATE path.
  const rearmCandidates = () => {
    if (savedIceCandidate) pc.onicecandidate = savedIceCandidate;
  };

  const send = (type, payload) => {
    try {
      peer.socket.send({ type, payload, dst: remoteId });
      return true;
    } catch (e) {
      return false;
    }
  };

  const armWatchdog = () => {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (destroyed || !recovering) return;
      const st = pc.iceConnectionState;
      if (st === 'connected' || st === 'completed') { succeed(); return; }
      // Offerer may retry; answerer just gives up (the offerer drives).
      if (role === 'offerer' && attempts < MAX_ATTEMPTS) {
        recovering = false;
        startOffer('watchdog-retry');
      } else {
        giveUp('watchdog');
      }
    }, role === 'offerer' ? RESTART_TIMEOUT_MS : ANSWERER_WAIT_MS);
  };

  async function startOffer(reason) {
    if (destroyed || recovering) return;
    if (attempts >= MAX_ATTEMPTS) return giveUp('attempts-exhausted');
    recovering = true;
    attempts += 1;
    track(EVENTS.ICE_RESTART, { role, outcome: 'attempt', attempt: attempts, reason });
    try {
      rearmCandidates();
      // Push the peer's current ICE servers onto this live connection before
      // restarting. ice.js keeps peer.options.config fresh but PeerJS only reads
      // it when building a *new* connection, so without this a restart would
      // reuse possibly-expired TURN credentials.
      if (typeof pc.setConfiguration === 'function' && peer.options && peer.options.config) {
        try { pc.setConfiguration(peer.options.config); } catch (e) { /* keep existing */ }
      }
      if (typeof pc.restartIce === 'function') pc.restartIce();
      // iceRestart covers browsers without restartIce(). The transceivers set up
      // by the original peer.call are already on the pc, so a plain offer
      // preserves them — no legacy offerToReceive* flags, which only risk quirks
      // when mixed with transceivers (notably on Safari).
      const offer = await pc.createOffer({ iceRestart: true });
      if (destroyed) return;
      await pc.setLocalDescription(offer);
      if (destroyed) return;
      if (!send(MSG_OFFER, { connectionId, sdp: pc.localDescription })) {
        return giveUp('send-failed');
      }
      armWatchdog();
    } catch (e) {
      giveUp('offer-error');
    }
  }

  const controller = {
    isRecovering: () => recovering,

    // Called by the app's connection-state handlers (and internally from the ICE
    // override) when the link looks down. Idempotent while a restart is in flight.
    recover(reason) {
      if (destroyed || recovering) return;
      if (role === 'offerer') {
        startOffer(reason);
      } else {
        // Answerer: wait for the offerer's restart offer; give up if none lands.
        recovering = true;
        track(EVENTS.ICE_RESTART, { role, outcome: 'await_offer', reason });
        armWatchdog();
      }
    },

    async _onRemoteOffer(src, payload) {
      if (destroyed || role !== 'answerer') return;
      if (src && remoteId && src !== remoteId) return;
      recovering = true;
      try {
        rearmCandidates();
        await pc.setRemoteDescription(payload.sdp);
        if (destroyed) return;
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        if (destroyed) return;
        send(MSG_ANSWER, { connectionId, sdp: pc.localDescription });
        armWatchdog();
      } catch (e) {
        giveUp('answer-error');
      }
    },

    async _onRemoteAnswer(src, payload) {
      if (destroyed || role !== 'offerer' || !recovering) return;
      if (src && remoteId && src !== remoteId) return;
      try {
        await pc.setRemoteDescription(payload.sdp);
        // Success is confirmed by the ICE state reaching 'connected'; the
        // watchdog is the backstop if it never does.
      } catch (e) {
        giveUp('set-answer-error');
      }
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      clearTimers();
      if (controllers.get(connectionId) === controller) controllers.delete(connectionId);
      // Best-effort restore so a lingering pc behaves as PeerJS expects.
      try { pc.oniceconnectionstatechange = savedIceState; } catch (e) { /* pc gone */ }
    },
  };

  pc.oniceconnectionstatechange = (event) => {
    const st = pc.iceConnectionState;
    if (st === 'failed') {
      controller.recover('ice-failed');
      return; // never forward: PeerJS's handler would close the connection
    }
    if (st === 'disconnected') {
      clearTimeout(graceTimer);
      graceTimer = setTimeout(() => {
        const now = pc.iceConnectionState;
        if (now === 'disconnected' || now === 'failed') controller.recover('ice-disconnected');
      }, DISCONNECT_GRACE_MS);
      return; // PeerJS only logs 'disconnected'; safe not to forward
    }
    if (st === 'connected' || st === 'completed') {
      if (recovering) succeed();
      else attempts = 0; // a healthy stretch refreshes the restart budget
    }
    if (typeof savedIceState === 'function') savedIceState.call(pc, event);
  };

  controllers.set(connectionId, controller);
  return controller;
}
